import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAdminMarket } from "@/hooks/useAdminMarket";

export function useBureaucracyDocuments(type?: string) {
  const queryClient = useQueryClient();
  const { market } = useAdminMarket();

  const documentsQuery = useQuery({
    queryKey: ["bureaucracy-documents", type, market],
    queryFn: async () => {
      let query = supabase
        .from("bureaucracy_documents")
        .select("*")
        .eq("market", market)
        .order("created_at", { ascending: false });
      if (type) query = query.eq("type", type);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const alertsQuery = useQuery({
    queryKey: ["bureaucracy-alerts", market],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bureaucracy_alerts")
        .select("*")
        .eq("market", market)
        .order("alert_date", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const createDocument = useMutation({
    mutationFn: async (doc: Record<string, unknown>) => {
      const { data: userData } = await supabase.auth.getUser();
      const insertData = { ...doc, created_by: userData.user?.id, market } as any;
      const { data, error } = await supabase
        .from("bureaucracy_documents")
        .insert(insertData)
        .select()
        .single();
      if (error) throw error;

      // Auto-generate alerts if end_date exists
      if (data.end_date) {
        const endDate = new Date(data.end_date);
        const alertDays = [90, 30, 7];
        const alerts = alertDays.map((days) => {
          const alertDate = new Date(endDate);
          alertDate.setDate(alertDate.getDate() - days);
          return {
            document_id: data.id,
            alert_type: "expiration",
            alert_date: alertDate.toISOString().split("T")[0],
            priority: days <= 7 ? "urgent" : days <= 30 ? "high" : "medium",
            days_before_trigger: days,
            market,
          };
        }).filter((a) => new Date(a.alert_date) >= new Date());

        if (alerts.length > 0) {
          await supabase.from("bureaucracy_alerts").insert(alerts);
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bureaucracy-documents"] });
      queryClient.invalidateQueries({ queryKey: ["bureaucracy-alerts"] });
      toast.success("Documento criado com sucesso");
    },
    onError: () => toast.error("Erro ao criar documento"),
  });

  const updateDocument = useMutation({
    mutationFn: async ({ id, ...doc }: Record<string, unknown>) => {
      const { error } = await supabase
        .from("bureaucracy_documents")
        .update(doc as any)
        .eq("id", id as string);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bureaucracy-documents"] });
      toast.success("Documento atualizado");
    },
    onError: () => toast.error("Erro ao atualizar documento"),
  });

  const deleteDocument = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("bureaucracy_documents").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bureaucracy-documents"] });
      queryClient.invalidateQueries({ queryKey: ["bureaucracy-alerts"] });
      toast.success("Documento eliminado");
    },
    onError: () => toast.error("Erro ao eliminar documento"),
  });

  return {
    documents: documentsQuery.data || [],
    alerts: alertsQuery.data || [],
    isLoading: documentsQuery.isLoading,
    createDocument,
    updateDocument,
    deleteDocument,
  };
}
